import React from 'react';
import { useTranslation } from 'react-i18next';

type ItemStatus = 'waiting' | 'generating' | 'completed' | 'error' | 'fetching' | 'skipped';

export interface StreamingProgressItem {
  id: string | number;
  title: string;
  status: ItemStatus | string;
  content?: string;
  showCharCount?: boolean;
}

export interface StreamingModalProps {
  title: string;
  items: StreamingProgressItem[];
  variant?: 'default' | 'grid' | 'compact';
  showContent?: boolean;
  onClose?: () => void;
  className?: string;
}

const getProgressWidth = (status: string): string => {
  switch (status) {
    case 'waiting':
    case 'Waiting...':
      return '0%';
    case 'generating':
    case 'Generating...':
    case 'fetching':
    case 'Fetching...':
      return '50%';
    case 'completed':
    case 'Complete':
    case 'Complete!':
    case 'skipped':
    case 'Skipped':
      return '100%';
    case 'error':
    case 'Error':
    case 'Error!':
      return '100%';
    default:
      return '0%';
  }
};

const getProgressColor = (status: string): string => {
  if (status === 'error' || status === 'Error' || status === 'Error!') {
    return 'bg-red-500';
  }
  return 'bg-blue-500';
};

const StreamingProgressItem: React.FC<{
  item: StreamingProgressItem;
  showContent?: boolean;
}> = ({ item, showContent }) => {
  const { t } = useTranslation();

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'waiting':
        return t('common.waiting');
      case 'generating':
        return t('common.generating');
      case 'completed':
        return t('common.completed');
      case 'error':
        return t('common.error');
      case 'fetching':
        return t('common.fetching');
      case 'skipped':
        return t('common.skipped');
      default:
        return status;
    }
  };

  return (
    <div className="border rounded-lg dark:border-gray-700">
      <div className="p-3 bg-gray-50 dark:bg-gray-750">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium text-gray-700 dark:text-gray-300">{item.title}</h3>
          <div className="flex items-center gap-4">
            {item.showCharCount && item.content && (
              <span className="text-sm text-gray-500">{item.content.length} chars</span>
            )}
            <span className="text-sm text-gray-500">{getStatusLabel(item.status)}</span>
          </div>
        </div>
        <div className="relative h-2 bg-gray-200 rounded dark:bg-gray-700">
          <div
            className={`h-2 rounded transition-all duration-300 ${getProgressColor(item.status)}`}
            style={{ width: getProgressWidth(item.status) }}
          />
        </div>
      </div>
      {showContent && item.content && (
        <div className="p-4">
          <div className="prose dark:prose-invert max-w-none text-sm whitespace-pre-wrap">
            {item.content}
          </div>
        </div>
      )}
    </div>
  );
};

export const StreamingModal: React.FC<StreamingModalProps> = ({
  title,
  items,
  variant = 'default',
  showContent = false,
  onClose,
  className = '',
}) => {
  const getMaxWidthClass = (): string => {
    switch (variant) {
      case 'grid':
        return 'max-w-4xl';
      case 'compact':
        return 'max-w-md';
      default:
        return 'max-w-md';
    }
  };

  const renderItems = () => {
    if (variant === 'grid') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.map((item) => (
            <StreamingProgressItem key={item.id} item={item} showContent={showContent} />
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {items.map((item) => (
          <StreamingProgressItem key={item.id} item={item} showContent={showContent} />
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full mx-4 max-h-[90vh] overflow-y-auto ${getMaxWidthClass()} ${className}`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">{title}</h2>
        </div>

        {renderItems()}

        {onClose && (
          <div className="mt-4 text-right">
            <button
              onClick={onClose}
              className="px-2 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Legacy exports for backward compatibility
// ProductDescription uses this interface
export interface LegacyProductStreamingModalProps {
  productStatus: string;
  categoryStatus: string;
}

export const ProductStreamingModal: React.FC<LegacyProductStreamingModalProps> = ({
  productStatus,
  categoryStatus,
}) => {
  const { t } = useTranslation();

  const items: StreamingProgressItem[] = [
    {
      id: 'product',
      title: t('product.product_url'),
      status: productStatus,
    },
    {
      id: 'category',
      title: t('product.category_url'),
      status: categoryStatus,
    },
  ];

  return <StreamingModal title={t('common.fetching_content')} items={items} variant="compact" />;
};

// Default export for legacy imports
export default ProductStreamingModal;
